import { usePageTitle } from "@/hooks/use-page-title";
import { AuditLogTable } from "@/components/audit-log-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Search, Filter, Calendar, Loader2 } from "lucide-react";
import { useState, useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import type { AuditLog } from "@shared/schema";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { PageAbout } from "@/components/page-about";

async function exportAuditLogs(eventType?: string, severity?: string): Promise<void> {
  const params = new URLSearchParams();
  if (eventType && eventType !== "all") params.append("eventType", eventType);
  if (severity && severity !== "all") params.append("severity", severity);
  
  const queryString = params.toString();
  const url = `/api/audit-logs/export${queryString ? `?${queryString}` : ""}`;
  
  const response = await fetch(url, { credentials: "include" });
  
  if (!response.ok) {
    throw new Error("Failed to export audit logs");
  }
  
  const blob = await response.blob();
  const contentDisposition = response.headers.get("Content-Disposition");
  let filename = "audit-logs-export.csv";
  
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/);
    if (match) filename = match[1];
  }
  
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(downloadUrl);
  document.body.removeChild(a);
}

export default function Audit() {
  usePageTitle("Audit Log", "Security audit trail for all critical operations on the HealthWallet platform.");
  const { data: allAuditLogs = [], isLoading } = useQuery<AuditLog[]>({
    queryKey: ["/api/audit-logs"],
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportAuditLogs(eventTypeFilter, severityFilter);
      toast({
        title: "Export Complete",
        description: "Audit logs have been exported successfully.",
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Failed to export audit logs. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const filteredLogs = useMemo(() => {
    return allAuditLogs.filter((log) => {
      const matchesSearch =
        searchQuery === "" ||
        log.actor.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.resourceType.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.resourceId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (log.details?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);

      const matchesEventType =
        eventTypeFilter === "all" || log.eventType === eventTypeFilter;

      const matchesSeverity =
        severityFilter === "all" || log.severity === severityFilter;

      return matchesSearch && matchesEventType && matchesSeverity;
    });
  }, [searchQuery, eventTypeFilter, severityFilter]);

  return (
    <div className="space-y-6">
      <PageAbout
        title="Audit Logs"
        description="Complete, immutable audit trail of all system activities for security compliance. Every action is logged with timestamps, user information, and detailed context."
        features={[
          "Complete audit trail of all wallet transactions and user actions",
          "Filter by event type, severity, actor, or date range",
          "Immutable logs that cannot be modified or deleted",
          "Export logs to CSV for external compliance audits",
          "Real-time logging of all funding and payment activities"
        ]}
        tips={[
          "Use severity filters to focus on warnings and errors",
          "Export filtered results for compliance documentation",
          "Search by resource ID to trace specific wallet or transaction history"
        ]}
      />
      
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Audit Logs</h1>
          <p className="text-muted-foreground mt-1">Complete activity tracking</p>
        </div>
        <Button 
          onClick={handleExport} 
          disabled={isExporting}
          data-testid="button-export-logs"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          {isExporting ? "Exporting..." : "Export Logs"}
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex gap-4 flex-wrap">
            <div className="relative flex-1 min-w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search logs by actor, action, resource..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="input-search"
              />
            </div>
            <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
              <SelectTrigger className="w-48" data-testid="select-event-type">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Event Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                <SelectItem value="authorization">Authorization</SelectItem>
                <SelectItem value="wallet">Wallet</SelectItem>
                <SelectItem value="payment">Payment</SelectItem>
                <SelectItem value="data_export">Data Export</SelectItem>
                <SelectItem value="system">System</SelectItem>
                <SelectItem value="compliance">Compliance</SelectItem>
              </SelectContent>
            </Select>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-48" data-testid="select-severity">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" data-testid="button-date-range">
              <Calendar className="h-4 w-4 mr-2" />
              Date Range
            </Button>
          </div>
        </CardContent>
      </Card>

      <AuditLogTable logs={filteredLogs} />

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div>
          {isLoading ? "Loading..." : `Showing ${filteredLogs.length} of ${allAuditLogs.length} events`}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled data-testid="button-previous">
            Previous
          </Button>
          <Button variant="outline" size="sm" disabled data-testid="button-next">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
